/**
 * SQL compiler family base + dialect contract.
 *
 * `SqlCompiler` owns the orchestration shared by all SQL engines (how a plan
 * becomes SELECT/WHERE/GROUP BY/ORDER BY/LIMIT). Engine differences live in a
 * `SqlDialect`. To add a SQL engine, implement a dialect and construct a
 * `SqlCompiler(new YourDialect())` — the shared flow does not change.
 */
import type {
    QueryCompiler,
    SqlMutationResult,
    SqlTranspileResult,
} from '../compiler';
import { buildOutputMetadata } from '../../metadata/output-metadata-builder';
import type { MutationPlan } from '../../plan/mutation-plan';
import type { AssignmentOp } from '../../plan/mutation-plan';
import {
    AggregationNode,
    FieldRefNode,
    FilterConditionNode,
    FilterNode,
    GroupFieldNode,
    GroupingNode,
    HavingConditionNode,
    HavingNode,
    LogicalFilterNode,
    LogicalHavingNode,
    NotFilterNode,
    NotHavingNode,
    QueryPlan,
    SourceNode,
} from '../../plan/query-plan';

/** Collects bound values while building a statement. */
export class SqlParamContext {
    /** Positional values (recommended executable form). */
    readonly values: unknown[] = [];
    /** ClickHouse-style named params (name → value). */
    readonly namedValues: Record<string, unknown> = {};
    private counter = 0;

    /** Reserve the next parameter slot; returns its 0-based index. */
    next(): number {
        return this.counter++;
    }
}

/**
 * Engine-specific SQL syntax/behavior. Implemented per engine
 * (`ClickHouseDialect`, `PostgresDialect`, …).
 */
export interface SqlDialect {
    readonly engine: 'postgresql' | 'clickhouse';

    /** Quote an identifier (e.g. PG `"col"`, ClickHouse `` `col` ``). */
    escapeIdentifier(identifier: string): string;

    /**
     * Render access to a nested value inside a JSON/object column, using the
     * node's `field` (base column) and `nestedPath` (keys). Engines diverge:
     * PostgreSQL uses `->`/`->>` with a type cast; ClickHouse uses
     * `JSONExtract<Type>`. Only called when `nestedPath` is non-empty.
     */
    renderFieldPath(field: FieldRefNode): string;

    /**
     * Bind a value and return the placeholder to inline in SQL. Positional
     * dialects (PostgreSQL) push to `ctx.values` and return `$n`; named
     * dialects (ClickHouse) add to `ctx.namedValues` and return `{pN:Type}`
     * (the ClickHouse type must be inferred from the value here).
     */
    bindValue(value: unknown, ctx: SqlParamContext): string;

    /** Serialize a value for the debug `plainQuery` (never used for execution). */
    renderLiteral(value: unknown): string;

    /** Date-group expression for a group field (e.g. `toStartOfMonth("d")`). */
    renderDateGroup(field: FieldRefNode, group: GroupFieldNode): string;

    /**
     * Assemble an INSERT from a pre-built `VALUES` fragment. When `conflict` is
     * set the insert is an upsert: PostgreSQL renders `ON CONFLICT (keys) DO
     * UPDATE`; ClickHouse ignores it (dedup happens at the table engine level).
     * `columns` are raw names — the dialect escapes them.
     */
    renderInsert(
        table: string,
        columns: string[],
        valuesSql: string,
        conflict: { keyColumns: string[] } | null,
    ): string;

    /**
     * Assemble a single UPDATE assignment (`col = …`). `value` is the emitted
     * placeholder for set/increment/append, or `null` for `removeField`.
     * Engines diverge on append (`array_append` vs `arrayPushBack`).
     */
    renderAssignment(
        column: string,
        op: AssignmentOp,
        value: string | null,
    ): string;

    /**
     * Assemble a DELETE statement from a pre-built WHERE (`null` = unfiltered).
     * PostgreSQL: `DELETE FROM t WHERE …`; ClickHouse: `ALTER TABLE t DELETE WHERE …`.
     */
    renderDelete(table: string, where: string | null): string;

    /**
     * Assemble an UPDATE statement from pre-built SET assignments and WHERE.
     * PostgreSQL: `UPDATE t SET … WHERE …`; ClickHouse: `ALTER TABLE t UPDATE … WHERE …`.
     */
    renderUpdate(
        table: string,
        assignments: string,
        where: string | null,
    ): string;
}

/** Emits a value into SQL: either a bound placeholder or a debug literal. */
type ValueEmitter = (value: unknown) => string;

/**
 * Shared SQL orchestration. The flow below is engine-agnostic; every
 * engine-specific fragment is delegated to `this.dialect`. A dialect
 * implementation plugs in via `new SqlCompiler(dialect)`.
 *
 * NOTE: per-group `limit` (top-N per group level) is not yet handled here — it
 * needs window/ranking (`ROW_NUMBER` CTEs) or ClickHouse `LIMIT n BY`. See the
 * `TODO(group-limits)` below.
 */
export abstract class SqlCompiler implements QueryCompiler<SqlTranspileResult> {
    constructor(protected readonly dialect: SqlDialect) {}

    compile(plan: QueryPlan): SqlTranspileResult {
        const ctx = new SqlParamContext();
        const bind: ValueEmitter = (value) =>
            this.dialect.bindValue(value, ctx);
        const literal: ValueEmitter = (value) =>
            this.dialect.renderLiteral(value);

        // Build the executable form first (populates ctx), then the debug form.
        const query = this.render(plan, bind);
        const plainQuery = this.render(plan, literal);

        return {
            engine: this.dialect.engine,
            kind: 'sql',
            query,
            plainQuery,
            values: ctx.values,
            namedValues: ctx.namedValues,
            metadata: buildOutputMetadata(plan, 'offset'),
        };
    }

    compileMutation(plan: MutationPlan): SqlMutationResult {
        const render = (emit: ValueEmitter): string => {
            const table = this.qualifiedTable(plan.source);
            if (plan.operation === 'insert') {
                // Build the VALUES tuples; the dialect assembles the statement
                // (and any ON CONFLICT upsert clause).
                const valuesSql = plan.rows
                    .map(
                        (row) =>
                            `(${row.map((value) => emit(value)).join(', ')})`,
                    )
                    .join(', ');
                return this.dialect.renderInsert(
                    table,
                    plan.columns,
                    valuesSql,
                    plan.conflictKeys
                        ? { keyColumns: plan.conflictKeys }
                        : null,
                );
            }
            if (plan.operation === 'delete') {
                const where = plan.filter
                    ? this.renderFilter(plan.filter, emit)
                    : null;
                return this.dialect.renderDelete(table, where);
            }
            // update: bind SET assignments first, then WHERE.
            const assignments = plan.assignments
                .map((a) => {
                    const col = this.dialect.escapeIdentifier(a.field.field);
                    const value = a.op === 'removeField' ? null : emit(a.value);
                    return this.dialect.renderAssignment(col, a.op, value);
                })
                .join(', ');
            const where = plan.filter
                ? this.renderFilter(plan.filter, emit)
                : null;
            return this.dialect.renderUpdate(table, assignments, where);
        };

        const ctx = new SqlParamContext();
        const query = render((value) => this.dialect.bindValue(value, ctx));
        const plainQuery = render((value) => this.dialect.renderLiteral(value));

        return {
            engine: this.dialect.engine,
            kind: 'sql',
            operation: plan.operation,
            query,
            plainQuery,
            values: ctx.values,
            namedValues: ctx.namedValues,
        };
    }

    /** Qualify the table with its schema when present: `"schema"."name"`. */
    private qualifiedTable(source: SourceNode): string {
        const name = this.dialect.escapeIdentifier(source.name);
        return source.schema
            ? `${this.dialect.escapeIdentifier(source.schema)}.${name}`
            : name;
    }

    /** Render a field reference: a plain identifier or a nested JSON path. */
    private col(node: FieldRefNode): string {
        return node.nestedPath && node.nestedPath.length > 0
            ? this.dialect.renderFieldPath(node)
            : this.dialect.escapeIdentifier(node.field);
    }

    private render(plan: QueryPlan, emit: ValueEmitter): string {
        const parts: string[] = [
            `SELECT ${this.renderSelect(plan)}`,
            `FROM ${this.qualifiedTable(plan.source)}`,
        ];
        if (plan.filter) {
            parts.push(`WHERE ${this.renderFilter(plan.filter, emit)}`);
        }
        if (plan.grouping) {
            // TODO(group-limits): honor per-level GroupFieldNode.limit
            // via ROW_NUMBER CTEs (default) or a dialect override (ClickHouse LIMIT BY).
            parts.push(`GROUP BY ${this.renderGroupBy(plan.grouping)}`);
        }
        if (plan.having) {
            parts.push(`HAVING ${this.renderHaving(plan.having, emit)}`);
        }
        if (plan.ordering.length > 0) {
            parts.push(`ORDER BY ${this.renderOrderBy(plan)}`);
        }
        parts.push(`LIMIT ${plan.limits.limit}`);
        if (plan.pagination.skip !== null) {
            parts.push(`OFFSET ${plan.pagination.skip}`);
        }
        return parts.join(' ');
    }

    private renderSelect(plan: QueryPlan): string {
        if (plan.grouping || plan.aggregations.length > 0) {
            const columns: string[] = [];
            for (const group of plan.grouping?.groups ?? []) {
                columns.push(
                    `${this.groupExpr(
                        group,
                    )} AS ${this.dialect.escapeIdentifier(group.outputName)}`,
                );
            }
            for (const agg of plan.aggregations) {
                columns.push(
                    `${this.aggExpr(agg)} AS ${this.dialect.escapeIdentifier(
                        agg.outputName,
                    )}`,
                );
            }
            return columns.join(', ');
        }
        if (plan.projection) {
            return plan.projection.fields
                .map((f) => {
                    const expr = this.col(f);
                    // Nested paths render to an expression with no natural
                    // column name; alias it with the dotted name so results are
                    // stable and match the output metadata.
                    return f.nestedPath && f.nestedPath.length > 0
                        ? `${expr} AS ${this.dialect.escapeIdentifier(
                              f.qualifiedName(),
                          )}`
                        : expr;
                })
                .join(', ');
        }
        return '*';
    }

    private renderGroupBy(grouping: GroupingNode): string {
        return grouping.groups.map((g) => this.groupExpr(g)).join(', ');
    }

    private groupExpr(group: GroupFieldNode): string {
        return group.granularity
            ? this.dialect.renderDateGroup(group.field, group)
            : this.col(group.field);
    }

    private aggExpr(agg: AggregationNode): string {
        if (agg.fn === 'count') {
            return agg.field ? `COUNT(${this.col(agg.field)})` : 'COUNT(*)';
        }
        const field = agg.field ? this.col(agg.field) : '';
        return `${agg.fn.toUpperCase()}(${field})`;
    }

    private renderFilter(node: FilterNode, emit: ValueEmitter): string {
        if (node instanceof FilterConditionNode) {
            return this.renderCondition(node, emit);
        }
        if (node instanceof LogicalFilterNode) {
            const op = node.operator === 'and' ? ' AND ' : ' OR ';
            return `(${node.children
                .map((c) => this.renderFilter(c, emit))
                .join(op)})`;
        }
        if (node instanceof NotFilterNode) {
            return `NOT (${this.renderFilter(node.child, emit)})`;
        }
        return '';
    }

    /**
     * Escape the LIKE wildcards `%` and `_` (and the escape char) so user input
     * in contains/startsWith/endsWith/notContains is matched literally.
     * PostgreSQL, ClickHouse and MySQL all use backslash as the LIKE escape
     * character; a dialect that differs (e.g. SQL Server) should override this.
     */
    protected escapeLikePattern(value: string): string {
        return value
            .split('\\')
            .join('\\\\')
            .split('%')
            .join('\\%')
            .split('_')
            .join('\\_');
    }

    private renderCondition(
        node: FilterConditionNode,
        emit: ValueEmitter,
    ): string {
        const col = this.col(node.field);
        switch (node.operator) {
            case 'eq':
                return `${col} = ${emit(node.value)}`;
            case 'neq':
                return `${col} != ${emit(node.value)}`;
            case 'gt':
                return `${col} > ${emit(node.value)}`;
            case 'gte':
                return `${col} >= ${emit(node.value)}`;
            case 'lt':
                return `${col} < ${emit(node.value)}`;
            case 'lte':
                return `${col} <= ${emit(node.value)}`;
            case 'in':
                return `${col} IN (${asArray(node.value)
                    .map((v) => emit(v))
                    .join(', ')})`;
            case 'notIn':
                return `${col} NOT IN (${asArray(node.value)
                    .map((v) => emit(v))
                    .join(', ')})`;
            case 'contains':
                return `${col} LIKE ${emit(
                    `%${this.escapeLikePattern(String(node.value))}%`,
                )}`;
            case 'notContains':
                return `${col} NOT LIKE ${emit(
                    `%${this.escapeLikePattern(String(node.value))}%`,
                )}`;
            case 'startsWith':
                return `${col} LIKE ${emit(
                    `${this.escapeLikePattern(String(node.value))}%`,
                )}`;
            case 'endsWith':
                return `${col} LIKE ${emit(
                    `%${this.escapeLikePattern(String(node.value))}`,
                )}`;
            case 'between': {
                const [low, high] = asArray(node.value);
                return `${col} BETWEEN ${emit(low)} AND ${emit(high)}`;
            }
            case 'exists':
            case 'isNotNull':
                return `${col} IS NOT NULL`;
            case 'isNull':
                return `${col} IS NULL`;
            default:
                return '';
        }
    }

    private renderHaving(node: HavingNode, emit: ValueEmitter): string {
        if (node instanceof HavingConditionNode) {
            const expr = this.aggExpr(node.aggregation);
            return this.renderComparison(expr, node.operator, node.value, emit);
        }
        if (node instanceof LogicalHavingNode) {
            const op = node.operator === 'and' ? ' AND ' : ' OR ';
            return `(${node.children
                .map((c) => this.renderHaving(c, emit))
                .join(op)})`;
        }
        if (node instanceof NotHavingNode) {
            return `NOT (${this.renderHaving(node.child, emit)})`;
        }
        return '';
    }

    private renderComparison(
        expr: string,
        operator: string,
        value: unknown,
        emit: ValueEmitter,
    ): string {
        const ops: Record<string, string> = {
            eq: '=',
            neq: '!=',
            gt: '>',
            gte: '>=',
            lt: '<',
            lte: '<=',
        };
        const sql = ops[operator];
        return sql ? `${expr} ${sql} ${emit(value)}` : expr;
    }

    private renderOrderBy(plan: QueryPlan): string {
        const groups = plan.grouping?.groups ?? [];
        return plan.ordering
            .map((order) => {
                const dir = order.direction === 'desc' ? 'DESC' : 'ASC';
                if (order.target.kind === 'aggregation') {
                    const target = this.dialect.escapeIdentifier(
                        order.target.aggregation.outputName,
                    );
                    return `${target} ${dir}`;
                }
                // When grouped, an order field must render as the GROUP BY
                // expression (e.g. toStartOfMonth("created")), not the raw
                // column — otherwise the column is not in GROUP BY (invalid SQL).
                const field = order.target.field;
                const group = groups.find((g) => g.field.field === field.field);
                const target = group ? this.groupExpr(group) : this.col(field);
                return `${target} ${dir}`;
            })
            .join(', ');
    }
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [value];
}
